import { Box, HStack, Text, VStack } from '@elkaso-app/web-design';
import React, { memo, useCallback, useState } from 'react';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';

interface MapPickerProps {
  lat: number | null;
  lng: number | null;
  onCoordinatesChange: (lat: number, lng: number) => void;
  height?: string;
}

// UAE center (Dubai)
const DEFAULT_CENTER = {
  lat: 25.2048,
  lng: 55.2708,
};

const MARKER_ICON = {
  path: 'M15.3579 35.6744C15.3579 19.8548 28.2724 10.25 40.6254 10.25C52.9783 10.25 65.8928 19.8548 65.8928 35.6744C65.8928 50.6465 43.4328 73.5285 42.5906 74.3759C42.0291 74.9409 41.4676 75.2234 40.6254 75.2234C39.7831 75.2234 39.2216 74.9409 38.6601 74.3759C37.8179 73.5285 15.3579 50.6465 15.3579 35.6744ZM40.9999 49.0536C48.2782 49.0536 54.1785 42.9894 54.1785 35.5089C54.1785 28.0284 48.2782 21.9643 40.9999 21.9643C33.7216 21.9643 27.8213 28.0284 27.8213 35.5089C27.8213 42.9894 33.7216 49.0536 40.9999 49.0536Z',
  fillColor: '#3182CE',
  fillOpacity: 1,
  anchor: { x: 41, y: 78 },
  strokeWeight: 0,
  scale: 0.8,
};

const GoogleMapComponent = withScriptjs(
  withGoogleMap((props: any) => {
    const { lat, lng, onCoordinatesChange } = props;

    const center = {
      lat: lat ?? DEFAULT_CENTER.lat,
      lng: lng ?? DEFAULT_CENTER.lng,
    };

    const handleMapClick = useCallback(
      (event: any) => {
        onCoordinatesChange(event.latLng.lat(), event.latLng.lng());
      },
      [onCoordinatesChange]
    );

    const handleMarkerDragEnd = useCallback(
      (event: any) => {
        onCoordinatesChange(event.latLng.lat(), event.latLng.lng());
      },
      [onCoordinatesChange]
    );

    const mapProps: any = {
      zoom: 10,
      center,
      onClick: handleMapClick,
      defaultOptions: {
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      },
    };

    return (
      <GoogleMap {...mapProps}>
        {lat !== null && lng !== null && (
          <Marker
            position={{ lat, lng }}
            icon={MARKER_ICON as any}
            draggable={true}
            onDragEnd={handleMarkerDragEnd}
          />
        )}
      </GoogleMap>
    );
  })
);

export const MapPicker: React.FC<MapPickerProps> = memo(({ lat, lng, onCoordinatesChange, height = '300px' }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleCoordinatesChange = useCallback(
    (newLat: number, newLng: number) => {
      setIsDragging(true);
      onCoordinatesChange(newLat, newLng);
      setTimeout(() => setIsDragging(false), 100);
    },
    [onCoordinatesChange]
  );

  return (
    <VStack spacing={2} align='stretch'>
      <Box border='1px' borderColor='gray.200' borderRadius='md' overflow='hidden' position='relative'>
        {isDragging && (
          <Box
            position='absolute'
            top={2}
            right={2}
            bg='blue.500'
            color='white'
            px={2}
            py={1}
            borderRadius='md'
            fontSize='xs'
            zIndex={1000}>
            Updating...
          </Box>
        )}

        <GoogleMapComponent
          googleMapURL={`https://maps.googleapis.com/maps/api/js?key=AIzaSyD0eC_j5MVeV5YsOIRnLVRyIx9npurleUA&libraries=places&language=en`}
          loadingElement={<div style={{ height: '100%' }} />}
          containerElement={<div style={{ height, borderRadius: '8px', overflow: 'hidden' }} />}
          mapElement={<div style={{ height: '100%' }} />}
          {...({ lat, lng, onCoordinatesChange: handleCoordinatesChange } as any)}
        />
      </Box>

      {lat !== null && lng !== null && (
        <HStack spacing={4} fontSize='xs' color='gray.500'>
          <Text>Lat: {lat.toFixed(6)}</Text>
          <Text>Lng: {lng.toFixed(6)}</Text>
        </HStack>
      )}
    </VStack>
  );
});

MapPicker.displayName = 'MapPicker';
